import exactdnMap from '../data/exactdn-map.json' with { type: 'json' }
import seoOverridesJson from '../data/seo-overrides.json' with { type: 'json' }
import { stripStaleTrackingHtml } from './affiliate.ts'

export type TextFix = { find: string; replace: string }
export type HtmlFix = { find: string; replace: string }

const seoOverrides = seoOverridesJson as Record<string, { textFixes?: TextFix[]; htmlFixes?: HtmlFix[] }>

const PAGE_MAP: Record<string, string> = {
  '/look-book/': '/look-books/',
  '/lookbooks/': '/look-books/',
  '/2dos-archive/': '/2dos/',
  '/trends-archive/': '/trends/',
  '/the-supers/': '/trends/',
  '/search/erewhon/': '/food/',
  '/shop-tigg-picks/': '/closet/',
  '/shop-my-closet/': '/closet/',
  '/shop/': '/closet/',
  '/store/': '/closet/',
  '/cart/': '/closet/',
  '/checkout/': '/closet/',
}

const SLUG_ALIASES: Record<string, string> = {
  'how-to-make-the-hailey-bieber-smoothie-at-home': 'i-tried-the-hailey-bieber-erewhon-smoothie-so-you-dont-have-to',
}

const PREFIX_FALLBACK: Record<string, string> = {
  lookbooks: '/look-books/',
  'look-book': '/look-books/',
  'look-books': '/look-books/',
  trends: '/trends/',
  '2dos': '/2dos/',
  travel: '/travel/',
  food: '/food/',
  beauty: '/beauty/',
  wellness: '/wellness/',
  style: '/trends/',
  supers: '/trends/',
  contributed: '/stories/',
  guest_blogs: '/stories/',
  product: '/closet/',
  shop: '/closet/',
}

const LIVE_SECTIONS = new Set([
  '/look-books/',
  '/trends/',
  '/2dos/',
  '/travel/',
  '/food/',
  '/beauty/',
  '/wellness/',
  '/stories/',
  '/closet/',
  '/about/',
  '/newsletter/',
  '/contact/',
  '/contributors/',
  '/press/',
  '/terms/',
  '/privacy/',
])

const SHORTCODE = /\[(?:tigg_[^\]]*|gallery[^\]]*|caption[^\]]*|embed[^\]]*|product[^\]]*)\]/gi

const LEGACY = exactdnMap as Record<string, string>
const MISSING_SUNSET_SHOT = /Screenshot-2024-01-24-at-9\.17\.00/i

function stripMissingLegacyImages(html: string) {
  return html
    .replace(/<div class="wp-block-image">\s*<figure\b[^>]*>[\s\S]*?<\/figure>\s*<\/div>/gi, (block) =>
      MISSING_SUNSET_SHOT.test(block) ? '' : block,
    )
    .replace(/<figure\b[^>]*>[\s\S]*?<\/figure>/gi, (block) => (MISSING_SUNSET_SHOT.test(block) ? '' : block))
    .replace(/<img\b[^>]*>/gi, (tag) => (MISSING_SUNSET_SHOT.test(tag) ? '' : tag))
}

function rewriteLegacyMedia(html: string) {
  const withLocal = html.replace(/https:\/\/ektfasqp5br\.exactdn\.com\/[^\\"'\s)]+/gi, (url) => {
    try {
      const parsed = new URL(url)
      return LEGACY[`${parsed.origin}${parsed.pathname}`] || url
    } catch {
      return url
    }
  })
  return withLocal.replace(/<img\b([^>]*)>/gi, (_tag, attrs: string) => {
    let next = attrs
    if (!/\sloading\s*=/i.test(next)) next += ' loading="lazy"'
    if (!/\sdecoding\s*=/i.test(next)) next += ' decoding="async"'
    return `<img${next}>`
  })
}

function withSlash(pathname: string) {
  if (!pathname || pathname === '/') return pathname || '/'
  if (/\.[a-z0-9]+$/i.test(pathname.split('/').pop() || '')) return pathname
  return pathname.endsWith('/') ? pathname : `${pathname}/`
}

function isTiggHost(hostname: string) {
  return /^(www\.)?theitgirlguide\.com$/i.test(hostname)
}

export function rewriteTiggHref(href: string, slugs: Iterable<string>) {
  const trimmed = href.trim()
  if (!trimmed || /^(mailto:|tel:|javascript:|#)/i.test(trimmed)) return href

  let url: URL
  try {
    url = trimmed.startsWith('/') ? new URL(trimmed, 'https://theitgirlguide.com') : new URL(trimmed)
  } catch {
    return href
  }

  if (!trimmed.startsWith('/') && !isTiggHost(url.hostname)) return href

  const path = withSlash(url.pathname)
  if (PAGE_MAP[path]) return `${PAGE_MAP[path]}${url.search}${url.hash}`
  if (LIVE_SECTIONS.has(path) || path === '/') return trimmed.startsWith('/') ? `${path}${url.search}${url.hash}` : href

  const slugSet = slugs instanceof Set ? slugs : new Set(slugs)
  const parts = path.replace(/\/+$/, '').split('/').filter(Boolean)
  const last = parts[parts.length - 1] || ''
  const alias = SLUG_ALIASES[last]
  if (alias && slugSet.has(alias)) return `/stories/${alias}/`
  if (last && slugSet.has(last) && parts[0] !== 'author') return `/stories/${last}/`

  const fallback = PREFIX_FALLBACK[parts[0] || '']
  if (fallback && parts.length >= 2) return fallback
  return href
}

export function rewriteArticleHtml(html: string, slugs: Iterable<string>) {
  return rewriteLegacyMedia(stripMissingLegacyImages(html))
    .replace(/<h1(\b[^>]*)>/gi, '<h2$1>')
    .replace(/<\/h1>/gi, '</h2>')
    .replace(SHORTCODE, '')
    .replace(/href=(["'])([^"']+)\1/gi, (full, quote: string, href: string) => {
      const next = rewriteTiggHref(href, slugs)
      return next === href ? full : `href=${quote}${next}${quote}`
    })
}

function classNames(attrs: string) {
  return attrs.match(/\bclass\s*=\s*(["'])([\s\S]*?)\1/i)?.[2]?.split(/\s+/).filter(Boolean) || []
}

function addClass(attrs: string, name: string) {
  if (classNames(attrs).includes(name)) return attrs
  if (/\bclass\s*=/i.test(attrs)) {
    return attrs.replace(/\bclass\s*=\s*(["'])([\s\S]*?)\1/i, (_full, quote: string, value: string) => {
      return `class=${quote}${value} ${name}${quote}`
    })
  }
  return `${attrs} class="${name}"`
}

function isSingleProductLink(inner: string) {
  const stripped = inner
    .trim()
    .replace(/<\/?(?:em|strong|mark|span)\b[^>]*>/gi, '')
    .trim()
  if (!/^<a\b[^>]*>[\s\S]*<\/a>$/i.test(stripped)) return false
  return (stripped.match(/<a\b/gi) || []).length === 1
}

const CAPTION_TRIPLET =
  /<h4\b([^>]*)>([\s\S]*?)<\/h4>(\s*)<h5\b([^>]*)>([\s\S]*?)<\/h5>(\s*)<h6\b([^>]*)>([\s\S]*?)<\/h6>/gi

function demoteCaption(level: 4 | 5 | 6, attrs: string, inner: string) {
  return `<p${addClass(attrs, `tigg-caption-h${level}`)}>${inner}</p>`
}

/**
 * Product captions are a consecutive h4 + h5 + h6: a single product link,
 * a retailer link, then a price line, each with class wp-block-heading.
 * Section headings (h1-h3, and h4+ that are not that triplet) stay headings.
 */
export function demoteCaptionHeadings(html: string) {
  if (!html) return html
  return html.replace(
    CAPTION_TRIPLET,
    (full, h4attrs: string, h4inner: string, gap45: string, h5attrs: string, h5inner: string, gap56: string, h6attrs: string, h6inner: string) => {
      const classes = [h4attrs, h5attrs, h6attrs]
      if (!classes.every((attrs) => classNames(attrs).includes('wp-block-heading'))) return full
      if (!isSingleProductLink(h4inner) || !/<a\b/i.test(h5inner)) return full
      return `${demoteCaption(4, h4attrs, h4inner)}${gap45}${demoteCaption(5, h5attrs, h5inner)}${gap56}${demoteCaption(6, h6attrs, h6inner)}`
    },
  )
}

function replaceExact(text: string, find: string, replace: string) {
  if (!find || find === replace || !text.includes(find)) return text
  return text.split(find).join(replace)
}

/**
 * Exact HTML replacements for one slug. Used when a fix has to move tag
 * boundaries, which text-node replacements cannot do. Runs after href
 * rewriting so the find string matches the markup the page already renders.
 */
export function applyHtmlFixes(html: string, fixes: HtmlFix[] | undefined) {
  const active = (fixes || []).filter((fix) => fix.find && fix.find !== fix.replace)
  if (!html || !active.length) return html
  let next = html
  for (const fix of active) {
    if (!next.includes(fix.find)) continue
    next = next.split(fix.find).join(fix.replace)
  }
  return next
}

/** Exact replacements in text nodes only. Tags, attributes, and URLs stay put. */
export function applyTextFixes(html: string, fixes: TextFix[] | undefined) {
  const active = (fixes || []).filter((fix) => fix.find)
  if (!html || !active.length) return html
  let skip = 0
  return html.replace(/(<[^>]+>)|([^<]+)/g, (_full, tag: string | undefined, text: string | undefined) => {
    if (tag) {
      const name = /^<\/?\s*([a-z0-9]+)/i.exec(tag)?.[1]?.toLowerCase()
      if (name === 'script' || name === 'style') {
        if (tag.startsWith('</')) skip = Math.max(0, skip - 1)
        else if (!tag.endsWith('/>')) skip += 1
      }
      return tag
    }
    if (!text || skip > 0) return text ?? ''
    let next = text
    for (const fix of active) next = replaceExact(next, fix.find, fix.replace)
    return next
  })
}

/** Article display HTML. Stored post bodies are left unchanged. */
export function renderArticleHtml(html: string, slugs: Iterable<string>, slug?: string) {
  const overrides = slug ? seoOverrides[slug] : undefined
  const rewritten = applyHtmlFixes(rewriteArticleHtml(html, slugs), overrides?.htmlFixes)
  const fixed = applyTextFixes(rewritten, overrides?.textFixes)
  return stripStaleTrackingHtml(demoteCaptionHeadings(fixed))
}
